import { lazy, type ComponentType } from 'react';
import { matchPath } from 'react-router-dom';

export { default as Index } from './pages/Index';
export { default as NotFound } from './pages/NotFound';

type Page = ComponentType<Record<string, unknown>>;

const page = (loader: () => Promise<{ default: Page }>): Page => lazy(loader);

export const ProductAtlas = page(() => import('./pages/ProductAtlas'));
export const Features = page(() => import('./pages/Features'));
export const Pricing = page(() => import('./pages/Pricing'));
export const About = page(() => import('./pages/About'));
export const Contact = page(() => import('./pages/Contact'));
export const PrivacyPolicy = page(() => import('./pages/PrivacyPolicy'));
export const TermsOfService = page(() => import('./pages/TermsOfService'));
export const CookiePolicy = page(() => import('./pages/CookiePolicy'));
export const DynamicLanding = page(() => import('./pages/DynamicLanding'));
export const BlogArticle = page(() => import('./pages/BlogArticle'));
export const BlogIndex = page(() => import('./pages/BlogIndex'));
export const Partners = page(() => import('./pages/Partners'));
export const AdminLegal = page(() => import('./pages/AdminLegal'));
export const GDPRPage = page(() => import('./pages/GDPRPage'));
export const DpaPage = page(() => import('./pages/DpaPage'));
export const BankConnectFormat = page(() => import('./pages/BankConnectFormat'));
export const MigrationGuide = page(() => import('./pages/MigrationGuide'));
export const Technology = page(() => import('./pages/Technology'));
export const AppLegalEmbed = page(() => import('./pages/AppLegalEmbed'));
export const StatusPage = page(() => import('./pages/StatusPage'));
export const ConstructionSoftware = page(() => import('./pages/ConstructionSoftware'));
export const TradeWebsites = page(() => import('./pages/TradeWebsites'));
export const PriceCalculator = page(() => import('./pages/PriceCalculator'));
export const PriceCalculatorPainter = page(() => import('./pages/PriceCalculatorPainter'));
export const PriceCalculatorFloor = page(() => import('./pages/PriceCalculatorFloor'));
export const RateQuiz = page(() => import('./pages/RateQuiz'));
export const QuizHub = page(() => import('./pages/QuizHub'));
export const EmployeeQuiz = page(() => import('./pages/EmployeeQuiz'));
export const VehicleQuiz = page(() => import('./pages/VehicleQuiz'));
export const QuoteQuiz = page(() => import('./pages/QuoteQuiz'));
export const MaterialQuiz = page(() => import('./pages/MaterialQuiz'));
export const HireQuiz = page(() => import('./pages/HireQuiz'));
export const WholeFirm = page(() => import('./pages/WholeFirm'));
export const AiAssistant = page(() => import('./pages/AiAssistant'));
export const ComparisonHub = page(() => import('./pages/ComparisonHub'));
export const ComparisonPage = page(() => import('./pages/ComparisonPage'));
export const Videos = page(() => import('./pages/Videos'));
export const SoloOffer = page(() => import('./pages/SoloOffer'));
export const TradeLanding = page(() => import('./pages/TradeLanding'));
export const CustomerPortalLanding = page(() => import('./pages/CustomerPortalLanding'));
export const BozpLanding = page(() => import('./pages/BozpLanding'));
export const ForAccountants = page(() => import('./pages/ForAccountants'));
export const AccountantLanding = page(() => import('./pages/AccountantLanding'));
export const DigitalBookkeeping = page(() => import('./pages/DigitalBookkeeping'));
export const Impressum = page(() => import('./pages/Impressum'));
export const FreeWorkQuiz = page(() => import('./pages/FreeWorkQuiz'));
export const LatePayerQuiz = page(() => import('./pages/LatePayerQuiz'));
export const QuoteTool = page(() => import('./pages/QuoteTool'));
export const QuoteToolMason = page(() => import('./pages/QuoteToolMason'));
export const QuoteToolCarpenter = page(() => import('./pages/QuoteToolCarpenter'));
export const WorldSurvive = page(() => import('./pages/WorldSurvive'));
export const WorldGrow = page(() => import('./pages/WorldGrow'));
export const BlackWorkArticle = page(() => import('./pages/BlackWorkArticle'));
export const AccountantArticle = page(() => import('./pages/AccountantArticle'));
export const RightsQuiz = page(() => import('./pages/RightsQuiz'));

export const AdminVideos: Page = import.meta.env.DEV
  ? lazy(() => import('./pages/AdminVideos'))
  : () => null;

export const AdminArticles: Page = import.meta.env.DEV
  ? lazy(() => import('./pages/AdminArticles'))
  : () => null;

const routeLoaders: Array<{ pattern: string; load: () => Promise<unknown> }> = [
  { pattern: '/atlas/', load: () => import('./pages/ProductAtlas') },
  { pattern: '/moduly/', load: () => import('./pages/Features') },
  { pattern: '/funktioner/', load: () => import('./pages/Features') },
  { pattern: '/features/', load: () => import('./pages/Features') },
  { pattern: '/moduly/*', load: () => import('./pages/DynamicLanding') },
  { pattern: '/funktioner/*', load: () => import('./pages/DynamicLanding') },
  { pattern: '/features/*', load: () => import('./pages/DynamicLanding') },
  { pattern: '/cennik/', load: () => import('./pages/Pricing') },
  { pattern: '/cenik/', load: () => import('./pages/Pricing') },
  { pattern: '/priser/', load: () => import('./pages/Pricing') },
  { pattern: '/pricing/', load: () => import('./pages/Pricing') },
  { pattern: '/o-nas/', load: () => import('./pages/About') },
  { pattern: '/om-os/', load: () => import('./pages/About') },
  { pattern: '/about/', load: () => import('./pages/About') },
  { pattern: '/kontakt/', load: () => import('./pages/Contact') },
  { pattern: '/contact/', load: () => import('./pages/Contact') },
  { pattern: '/blog/', load: () => import('./pages/BlogIndex') },
  { pattern: '/blog/*', load: () => import('./pages/BlogArticle') },
  { pattern: '/videa/', load: () => import('./pages/Videos') },
  { pattern: '/videoer/', load: () => import('./pages/Videos') },
  { pattern: '/videos/', load: () => import('./pages/Videos') },
  { pattern: '/partneri/', load: () => import('./pages/Partners') },
  { pattern: '/partnere/', load: () => import('./pages/Partners') },
  { pattern: '/partners/', load: () => import('./pages/Partners') },
  { pattern: '/terms/', load: () => import('./pages/TermsOfService') },
  { pattern: '/privacy/', load: () => import('./pages/PrivacyPolicy') },
  { pattern: '/cookies/', load: () => import('./pages/CookiePolicy') },
  { pattern: '/dpa/', load: () => import('./pages/DpaPage') },
  { pattern: '/security/', load: () => import('./pages/Technology') },
  { pattern: '/security/subprocessors/', load: () => import('./pages/AppLegalEmbed') },
  { pattern: '/security/data-map/', load: () => import('./pages/AppLegalEmbed') },
  { pattern: '/status/', load: () => import('./pages/StatusPage') },
  { pattern: '/gdpr/', load: () => import('./pages/GDPRPage') },
  { pattern: '/bank-connect/', load: () => import('./pages/BankConnectFormat') },
  { pattern: '/skift-til-bidmio/', load: () => import('./pages/MigrationGuide') },
  { pattern: '/stavebny-software/', load: () => import('./pages/ConstructionSoftware') },
  { pattern: '/stavebni-software/', load: () => import('./pages/ConstructionSoftware') },
  { pattern: '/byggesoftware/', load: () => import('./pages/ConstructionSoftware') },
  { pattern: '/webstranka-pre-remeselnika/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/webova-stranka-pro-remeslnika/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/hjemmeside-til-haandvaerker/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/website-for-tradespeople/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/webstranka-pre-stavebne-firmy/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/webova-stranka-pro-stavebni-firmy/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/hjemmeside-til-byggefirma/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/website-for-construction-companies/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/webstranka-pre-servisne-firmy/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/webova-stranka-pro-servisni-firmy/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/hjemmeside-til-servicefirma/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/website-for-service-firms/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/tvorba-webu-pre-remeselnikov/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/tvorba-webu-pro-remeslniky/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/vi-laver-hjemmesiden/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/website-creation-for-tradespeople/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/cenova-kalkulacka-pre-remeselnikov/', load: () => import('./pages/PriceCalculator') },
  { pattern: '/cenova-kalkulacka-pro-remeslniky/', load: () => import('./pages/PriceCalculator') },
  { pattern: '/prisberegner-til-haandvaerker/', load: () => import('./pages/PriceCalculator') },
  { pattern: '/prisberegner-til-maler/', load: () => import('./pages/PriceCalculatorPainter') },
  { pattern: '/cenova-kalkulacka-pro-malire/', load: () => import('./pages/PriceCalculatorPainter') },
  { pattern: '/cenova-kalkulacka-pre-maliarov/', load: () => import('./pages/PriceCalculatorPainter') },
  { pattern: '/prisberegner-til-gulvfirma/', load: () => import('./pages/PriceCalculatorFloor') },
  { pattern: '/price-calculator-for-tradespeople/', load: () => import('./pages/PriceCalculator') },
  { pattern: '/quiz/', load: () => import('./pages/QuizHub') },
  // One wildcard per quiz covers its hub, trade pages and result pages.
  { pattern: '/quiz/timepris-beregner/*', load: () => import('./pages/RateQuiz') },
  { pattern: '/quiz/tilbudstjek/*', load: () => import('./pages/QuoteQuiz') },
  { pattern: '/quiz/varebil-beregner/*', load: () => import('./pages/VehicleQuiz') },
  { pattern: '/quiz/medarbejder-beregner/*', load: () => import('./pages/EmployeeQuiz') },
  { pattern: '/quiz/gratis-arbejde-beregner/*', load: () => import('./pages/FreeWorkQuiz') },
  { pattern: '/quiz/betaler-beregner/*', load: () => import('./pages/LatePayerQuiz') },
  { pattern: '/quiz/foerste-ansatte-beregner/*', load: () => import('./pages/HireQuiz') },
  { pattern: '/quiz/materialeavance-beregner/*', load: () => import('./pages/MaterialQuiz') },
  // English site (bidmio.com, UK).
  { pattern: '/quiz/hourly-rate-calculator/*', load: () => import('./pages/RateQuiz') },
  { pattern: '/quiz/quote-check/*', load: () => import('./pages/QuoteQuiz') },
  { pattern: '/quiz/van-cost-calculator/*', load: () => import('./pages/VehicleQuiz') },
  { pattern: '/quiz/employee-cost-calculator/*', load: () => import('./pages/EmployeeQuiz') },
  { pattern: '/quiz/free-work-calculator/*', load: () => import('./pages/FreeWorkQuiz') },
  { pattern: '/quiz/late-payment-calculator/*', load: () => import('./pages/LatePayerQuiz') },
  { pattern: '/quiz/first-employee-calculator/*', load: () => import('./pages/HireQuiz') },
  { pattern: '/quiz/material-markup-calculator/*', load: () => import('./pages/MaterialQuiz') },
  // bidmio.de
  { pattern: '/quiz/stundensatz-rechner/*', load: () => import('./pages/RateQuiz') },
  { pattern: '/quiz/mitarbeiterkosten-rechner/*', load: () => import('./pages/EmployeeQuiz') },
  { pattern: '/quiz/transporter-rechner/*', load: () => import('./pages/VehicleQuiz') },
  { pattern: '/quiz/angebots-check/*', load: () => import('./pages/QuoteQuiz') },
  { pattern: '/quiz/zahlungsverzug-rechner/*', load: () => import('./pages/LatePayerQuiz') },
  { pattern: '/quiz/gratisarbeit-rechner/*', load: () => import('./pages/FreeWorkQuiz') },
  { pattern: '/quiz/erster-mitarbeiter-rechner/*', load: () => import('./pages/HireQuiz') },
  { pattern: '/quiz/materialaufschlag-rechner/*', load: () => import('./pages/MaterialQuiz') },
  // Czech (bidmio.cz) and Slovak (bidmio.sk) quizzes under /kviz/; the van and material slugs are the same in both.
  { pattern: '/kviz/', load: () => import('./pages/QuizHub') },
  { pattern: '/kviz/hodinova-sazba/*', load: () => import('./pages/RateQuiz') },
  { pattern: '/kviz/naklady-na-zamestnance/*', load: () => import('./pages/EmployeeQuiz') },
  { pattern: '/kviz/naklady-na-dodavku/*', load: () => import('./pages/VehicleQuiz') },
  { pattern: '/kviz/kontrola-nabidky/*', load: () => import('./pages/QuoteQuiz') },
  { pattern: '/kviz/neplatici-zakaznik/*', load: () => import('./pages/LatePayerQuiz') },
  { pattern: '/kviz/prace-zadarmo/*', load: () => import('./pages/FreeWorkQuiz') },
  { pattern: '/kviz/prvni-zamestnanec/*', load: () => import('./pages/HireQuiz') },
  { pattern: '/kviz/kalkulacka-hodinovej-sadzby/*', load: () => import('./pages/RateQuiz') },
  { pattern: '/kviz/kontrola-cenovej-ponuky/*', load: () => import('./pages/QuoteQuiz') },
  { pattern: '/kviz/cena-prace-zamestnanca/*', load: () => import('./pages/EmployeeQuiz') },
  { pattern: '/kviz/praca-zadarmo/*', load: () => import('./pages/FreeWorkQuiz') },
  { pattern: '/kviz/omeskane-platby/*', load: () => import('./pages/LatePayerQuiz') },
  { pattern: '/kviz/prvy-zamestnanec/*', load: () => import('./pages/HireQuiz') },
  { pattern: '/kviz/prirazka-na-material/*', load: () => import('./pages/MaterialQuiz') },
  { pattern: '/cela-firma-v-jednom/', load: () => import('./pages/WholeFirm') },
  { pattern: '/informacny-system-pre-firmu/', load: () => import('./pages/WholeFirm') },
  { pattern: '/hele-firmaet-i-et-system/', load: () => import('./pages/WholeFirm') },
  { pattern: '/the-whole-firm-in-one-system/', load: () => import('./pages/WholeFirm') },
  { pattern: '/construction-software/', load: () => import('./pages/ConstructionSoftware') },
  { pattern: '/ai-v-bidmio/', load: () => import('./pages/AiAssistant') },
  { pattern: '/ai-v-bidmiu/', load: () => import('./pages/AiAssistant') },
  { pattern: '/ai-i-bidmio/', load: () => import('./pages/AiAssistant') },
  { pattern: '/ai-in-bidmio/', load: () => import('./pages/AiAssistant') },
  { pattern: '/pre-zivnostnikov/', load: () => import('./pages/SoloOffer') },
  { pattern: '/ked-chyba-robota-a-peniaze/', load: () => import('./pages/WorldSurvive') },
  { pattern: '/ked-chyba-poriadok/', load: () => import('./pages/WorldGrow') },
  { pattern: '/praca-na-cierno/', load: () => import('./pages/BlackWorkArticle') },
  { pattern: '/uctovnictvo-a-uctovnicka/', load: () => import('./pages/AccountantArticle') },
  { pattern: '/kviz/povinnosti-a-prava/', load: () => import('./pages/RightsQuiz') },
  { pattern: '/naar-opgaverne-mangler/', load: () => import('./pages/WorldSurvive') },
  { pattern: '/naar-overblikket-mangler/', load: () => import('./pages/WorldGrow') },
  { pattern: '/sort-arbejde-i-byggeriet/', load: () => import('./pages/BlackWorkArticle') },
  { pattern: '/bogfoering-bilag-og-revisor/', load: () => import('./pages/AccountantArticle') },
  { pattern: '/quiz/pligter-og-rettigheder/', load: () => import('./pages/RightsQuiz') },
  { pattern: '/kdyz-chybi-prace-a-penize/', load: () => import('./pages/WorldSurvive') },
  { pattern: '/kdyz-chybi-poradek/', load: () => import('./pages/WorldGrow') },
  { pattern: '/prace-nacerno/', load: () => import('./pages/BlackWorkArticle') },
  { pattern: '/ucetnictvi-a-ucetni/', load: () => import('./pages/AccountantArticle') },
  { pattern: '/when-money-is-tight/', load: () => import('./pages/WorldSurvive') },
  { pattern: '/when-the-business-grows/', load: () => import('./pages/WorldGrow') },
  { pattern: '/cash-in-hand-work/', load: () => import('./pages/BlackWorkArticle') },
  { pattern: '/bookkeeping-and-your-accountant/', load: () => import('./pages/AccountantArticle') },
  { pattern: '/quiz/duties-and-rights/', load: () => import('./pages/RightsQuiz') },
  { pattern: '/wenn-das-geld-knapp-ist/', load: () => import('./pages/WorldSurvive') },
  { pattern: '/wenn-der-betrieb-waechst/', load: () => import('./pages/WorldGrow') },
  { pattern: '/schwarzarbeit/', load: () => import('./pages/BlackWorkArticle') },
  { pattern: '/buchhaltung-und-steuerberater/', load: () => import('./pages/AccountantArticle') },
  { pattern: '/quiz/pflichten-und-rechte/', load: () => import('./pages/RightsQuiz') },
  { pattern: '/pro-osvc/', load: () => import('./pages/SoloOffer') },
  { pattern: '/til-enkeltmandsvirksomhed/', load: () => import('./pages/SoloOffer') },
  { pattern: '/for-sole-traders/', load: () => import('./pages/SoloOffer') },
  { pattern: '/software-pre-elektrikarov/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-pre-murarov/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-til-murer/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-til-tagdaekker/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-pre-pokryvacov/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-pro-pokryvace/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-for-roofers/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-til-kloakmester/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-pro-elektrikare/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-til-elektriker/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-for-electricians/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-pre-instalaterov/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-pro-instalatery/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-til-vvs/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-for-plumbers/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-pre-maliarov/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-pro-malire/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-til-maler/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-for-painters/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-pre-stolarov-a-tesarov/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-pro-truhlare-a-tesare/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-til-toemrer/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-for-carpenters/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-pre-generalneho-dodavatela/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-pro-generalniho-dodavatele/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-til-hovedentreprenor/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-for-general-contractors/', load: () => import('./pages/TradeLanding') },
  { pattern: '/servisny-system/', load: () => import('./pages/TradeLanding') },
  { pattern: '/servisni-system/', load: () => import('./pages/TradeLanding') },
  { pattern: '/servicesystem/', load: () => import('./pages/TradeLanding') },
  { pattern: '/field-service-software/', load: () => import('./pages/TradeLanding') },
  { pattern: '/zakaznicky-portal/', load: () => import('./pages/CustomerPortalLanding') },
  { pattern: '/kundeportal/', load: () => import('./pages/CustomerPortalLanding') },
  { pattern: '/customer-portal/', load: () => import('./pages/CustomerPortalLanding') },
  { pattern: '/bozp-software/', load: () => import('./pages/BozpLanding') },
  { pattern: '/arbejdsmiljo/', load: () => import('./pages/BozpLanding') },
  { pattern: '/health-and-safety-software/', load: () => import('./pages/BozpLanding') },
  { pattern: '/pre-uctovnikov/', load: () => import('./pages/TradeLanding') },
  { pattern: '/pro-ucetni/', load: () => import('./pages/AccountantLanding') },
  { pattern: '/til-revisorer/', load: () => import('./pages/AccountantLanding') },
  { pattern: '/for-accountants/', load: () => import('./pages/AccountantLanding') },
  { pattern: '/digital-bogfoering/', load: () => import('./pages/DigitalBookkeeping') },
  { pattern: '/sammenlign/bidmio-vs-e-conomic/', load: () => import('./pages/ComparisonPage') },
  { pattern: '/porovnanie/', load: () => import('./pages/ComparisonHub') },
  { pattern: '/srovnani/', load: () => import('./pages/ComparisonHub') },
  { pattern: '/sammenlign/', load: () => import('./pages/ComparisonHub') },
  { pattern: '/porovnanie/*', load: () => import('./pages/ComparisonPage') },
  { pattern: '/srovnani/*', load: () => import('./pages/ComparisonPage') },
  { pattern: '/sammenlign/*', load: () => import('./pages/ComparisonPage') },
  { pattern: '/admin/legal/', load: () => import('./pages/AdminLegal') },
  { pattern: '/crmSystem/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/projektoveRiadenie/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/fakturovanie/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/kalendar/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/riadenieTimov/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/kalkulacie/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/cenovePonuky/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/pracovneVykazy/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/spravaDokladov/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/spravaTechniky/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/firemneVydavky/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/bezpecnost/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/kvalita/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/reporting/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/subkontraktor/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/stavebnyDennik/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/spracovanieDokladov/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/riadenieZamestancov/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/sledovanieMaterialov/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/skladoveHospodarstvo/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/majetokFirmy/', load: () => import('./pages/DynamicLanding') },
  { pattern: '/vozidlaTrasy/', load: () => import('./pages/DynamicLanding') },  // German (www.bidmio.de)
  { pattern: '/funktionen/*', load: () => import('./pages/DynamicLanding') },
  { pattern: '/preise/', load: () => import('./pages/Pricing') },
  { pattern: '/funktionen/', load: () => import('./pages/Features') },
  { pattern: '/ueber-uns/', load: () => import('./pages/About') },
  { pattern: '/partner/', load: () => import('./pages/Partners') },
  { pattern: '/bausoftware/', load: () => import('./pages/ConstructionSoftware') },
  { pattern: '/fuer-einzelunternehmer/', load: () => import('./pages/SoloOffer') },
  { pattern: '/software-fuer-elektriker/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-fuer-dachdecker/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-fuer-shk/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-fuer-maler/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-fuer-tischler-und-zimmerer/', load: () => import('./pages/TradeLanding') },
  { pattern: '/software-fuer-bauunternehmen/', load: () => import('./pages/TradeLanding') },
  { pattern: '/kundendienst-software/', load: () => import('./pages/TradeLanding') },
  { pattern: '/kundenportal/', load: () => import('./pages/CustomerPortalLanding') },
  { pattern: '/arbeitsschutz-software/', load: () => import('./pages/BozpLanding') },
  { pattern: '/fuer-steuerberater/', load: () => import('./pages/AccountantLanding') },
  { pattern: '/impressum/', load: () => import('./pages/Impressum') },
  { pattern: '/website-fuer-handwerker/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/website-fuer-baufirmen/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/website-erstellung-fuer-handwerker/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/website-fuer-servicefirmen/', load: () => import('./pages/TradeWebsites') },
  { pattern: '/preisrechner-fuer-handwerker/', load: () => import('./pages/PriceCalculator') },
  { pattern: '/der-ganze-betrieb-in-einem-system/', load: () => import('./pages/WholeFirm') },
  { pattern: '/ki-in-bidmio/', load: () => import('./pages/AiAssistant') },
  // Free quote tool for painters (one page per site).
  { pattern: '/gratis-tilbud/maler/', load: () => import('./pages/QuoteTool') },
  { pattern: '/cenova-ponuka-zadarmo/maliar/', load: () => import('./pages/QuoteTool') },
  { pattern: '/cenova-nabidka-zdarma/malir/', load: () => import('./pages/QuoteTool') },
  { pattern: '/kostenloses-angebot/maler/', load: () => import('./pages/QuoteTool') },
  { pattern: '/free-quote/painter-decorator/', load: () => import('./pages/QuoteTool') },
  { pattern: '/gratis-tilbud/murer/', load: () => import('./pages/QuoteToolMason') },
  { pattern: '/cenova-ponuka-zadarmo/obkladac/', load: () => import('./pages/QuoteToolMason') },
  { pattern: '/cenova-nabidka-zdarma/obkladac/', load: () => import('./pages/QuoteToolMason') },
  { pattern: '/kostenloses-angebot/fliesenleger/', load: () => import('./pages/QuoteToolMason') },
  { pattern: '/free-quote/tiler/', load: () => import('./pages/QuoteToolMason') },
  { pattern: '/gratis-tilbud/toemrer/', load: () => import('./pages/QuoteToolCarpenter') },
  { pattern: '/cenova-ponuka-zadarmo/tesar/', load: () => import('./pages/QuoteToolCarpenter') },
  { pattern: '/cenova-nabidka-zdarma/tesar/', load: () => import('./pages/QuoteToolCarpenter') },
  { pattern: '/kostenloses-angebot/zimmerer/', load: () => import('./pages/QuoteToolCarpenter') },
  { pattern: '/free-quote/carpenter/', load: () => import('./pages/QuoteToolCarpenter') },
];

const withSlash = (pathname: string): string => {
  if (pathname === '/') return pathname;
  return pathname.endsWith('/') ? pathname : `${pathname}/`;
};

export async function preloadRoute(pathname: string): Promise<void> {
  if (pathname === '/') return;

  const normalized = withSlash(pathname);
  const exact = routeLoaders.find(({ pattern }) => (
    !pattern.includes('*') && matchPath({ path: pattern, end: true }, normalized)
  ));
  if (exact) {
    await exact.load();
    return;
  }

  const wildcard = routeLoaders.find(({ pattern }) => (
    pattern.includes('*') && matchPath({ path: pattern, end: true }, normalized)
  ));
  if (wildcard) {
    await wildcard.load();
  }
}
